import { http } from '@/api/client/httpClient';
import type { PagedResult } from '@/api/types/abp';
import type {
  AddManualTransactionInput,
  ManualTransactionResultDto,
  ProcessProviderTransactionsInput,
  ProviderBoxesDto,
  ProviderDetailsDto,
  ProviderDetailsInput,
  ProviderPeriodActionInput,
  ProviderPeriodLockLogDto,
  ProviderPeriodLockResultDto,
  ProviderPeriodLockingViewDto,
  ProvidersSummaryInput,
  ProvidersSummaryRowDto,
  ProviderTransactionRowDto,
  ProviderTransactionsInput,
  ResultDto,
  SyncResponseCode,
} from '@/api/types/providerFunds';

// ABP conventional routing for ProvidersAssetsAppService / CorporateFundsAppService /
// ProvidersAssetsSyncAppService / ProviderPeriodLockingAppService.
//
// IMPORTANT: ABP picks the HTTP verb from the method-name PREFIX, not from whether the
// method takes a DTO. Every "Get*" method is therefore a GET, with the DTO bound from
// the query string (all these input DTOs are flat scalars, so axios `params` serialises
// them cleanly). The "Get" prefix is stripped and the rest kebab-cased for the route;
// non-Get prefixes (Add/Process/Lock/Unlock) default to POST and keep their full name.
// Calling a GET route with POST returns 405 Method Not Allowed.
//
//   GetMoneyProviders(dto)          -> GET  /money-providers
//   GetInternalProviders(dto)       -> GET  /internal-providers
//   GetCorporateFunds(dto)          -> GET  /corporate-funds
//   GetProviders()                  -> GET  /providers
//   GetProviderDetails(dto)         -> GET  /provider-details
//   GetBoxes(dto)                   -> GET  /boxes
//   GetTransactionsForDataTable(dto)-> GET  /transactions-for-data-table
//   AddManualTransaction(dto)       -> POST /add-manual-transaction
//   GetSummaryExcel(dto)            -> GET  /summary-excel        (blob)
//   GetTransactionsExcel(dto)       -> GET  /transactions-excel   (blob)
//   ProcessProviderTransactions(dto)-> POST /process-provider-transactions
//   GetPeriodStatus(dto)            -> GET  /period-status
//   GetAuditHistory(dto)            -> GET  /audit-history
//   Lock(dto) / Unlock(dto)         -> POST /lock, /unlock

function blobDownload(blob: Blob, fallbackFileName: string, disposition?: string) {
  const name = parseFileName(disposition) ?? fallbackFileName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function parseFileName(disposition: string | undefined): string | null {
  if (!disposition) return null;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/^"|"$/g, ''));
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1] ?? null;
}

// A set of methods common to both the Providers Assets and Corporate Funds services;
// they share the same DTOs and route tail, differing only in the controller base and
// the summary method name.
export interface ProviderFundsApi {
  base: string;
  getSummary: (input: ProvidersSummaryInput) => Promise<ProvidersSummaryRowDto[]>;
  // PA exposes a second grid (Internals); CF only has the Money grid.
  getInternalProviders?: (input: ProvidersSummaryInput) => Promise<ProvidersSummaryRowDto[]>;
  getProviders: () => Promise<ResultDto[]>;
  getProviderDetails: (input: ProviderDetailsInput) => Promise<ProviderDetailsDto>;
  getBoxes: (input: ProviderTransactionsInput) => Promise<ProviderBoxesDto>;
  getTransactionsForDataTable: (
    input: ProviderTransactionsInput,
  ) => Promise<PagedResult<ProviderTransactionRowDto>>;
  addManualTransaction: (input: AddManualTransactionInput) => Promise<ManualTransactionResultDto>;
  downloadSummaryExcel: (input: ProvidersSummaryInput, fallback: string) => Promise<void>;
  downloadTransactionsExcel: (input: ProviderTransactionsInput, fallback: string) => Promise<void>;
}

function common(base: string): Omit<ProviderFundsApi, 'base' | 'getSummary' | 'getInternalProviders'> {
  return {
    getProviders: () => http.get<ResultDto[]>(`${base}/providers`).then((r) => r.data),

    getProviderDetails: (input) =>
      http.get<ProviderDetailsDto>(`${base}/provider-details`, { params: input }).then((r) => r.data),

    getBoxes: (input) => http.get<ProviderBoxesDto>(`${base}/boxes`, { params: input }).then((r) => r.data),

    getTransactionsForDataTable: (input) =>
      http
        .get<PagedResult<ProviderTransactionRowDto>>(`${base}/transactions-for-data-table`, { params: input })
        .then((r) => r.data),

    addManualTransaction: (input) =>
      http.post<ManualTransactionResultDto>(`${base}/add-manual-transaction`, input).then((r) => r.data),

    downloadSummaryExcel: async (input, fallback) => {
      const res = await http.get<Blob>(`${base}/summary-excel`, { params: input, responseType: 'blob' });
      blobDownload(res.data, fallback, res.headers['content-disposition'] as string | undefined);
    },

    downloadTransactionsExcel: async (input, fallback) => {
      const res = await http.get<Blob>(`${base}/transactions-excel`, { params: input, responseType: 'blob' });
      blobDownload(res.data, fallback, res.headers['content-disposition'] as string | undefined);
    },
  };
}

const PA_BASE = '/api/app/providers-assets';
const CF_BASE = '/api/app/corporate-funds';
const SYNC_BASE = '/api/app/providers-assets-sync';
const LOCK_BASE = '/api/app/provider-period-locking';

export const providersAssetsApi: ProviderFundsApi = {
  base: PA_BASE,
  getSummary: (input) =>
    http.get<ProvidersSummaryRowDto[]>(`${PA_BASE}/money-providers`, { params: input }).then((r) => r.data),
  getInternalProviders: (input) =>
    http.get<ProvidersSummaryRowDto[]>(`${PA_BASE}/internal-providers`, { params: input }).then((r) => r.data),
  ...common(PA_BASE),
};

export const corporateFundsApi: ProviderFundsApi = {
  base: CF_BASE,
  getSummary: (input) =>
    http.get<ProvidersSummaryRowDto[]>(`${CF_BASE}/corporate-funds`, { params: input }).then((r) => r.data),
  ...common(CF_BASE),
};

export const providersAssetsSyncApi = {
  processProviderTransactions: (input: ProcessProviderTransactionsInput) =>
    http.post<SyncResponseCode>(`${SYNC_BASE}/process-provider-transactions`, input).then((r) => r.data),
};

export const providerPeriodLockingApi = {
  getProviders: () => http.get<ResultDto[]>(`${LOCK_BASE}/providers`).then((r) => r.data),
  getPeriodStatus: (providerId: string) =>
    http.get<ProviderPeriodLockingViewDto>(`${LOCK_BASE}/period-status`, { params: { providerId } }).then((r) => r.data),
  getAuditHistory: (providerId: string) =>
    http.get<ProviderPeriodLockLogDto[]>(`${LOCK_BASE}/audit-history`, { params: { providerId } }).then((r) => r.data),
  lock: (input: ProviderPeriodActionInput) =>
    http.post<ProviderPeriodLockResultDto>(`${LOCK_BASE}/lock`, input).then((r) => r.data),
  unlock: (input: ProviderPeriodActionInput) =>
    http.post<ProviderPeriodLockResultDto>(`${LOCK_BASE}/unlock`, input).then((r) => r.data),
};
